import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronDown, ChevronUp, GitMerge, History, LockKeyhole, PencilLine, Save, X } from 'lucide-react';
import { api } from '../../api';
import type { DirectoryChange, DirectoryCustomField } from '../../app/legacy-types';
import './directory-entity-details.css';

type EntityType = 'COMPANY' | 'CONTACT';
type SourceSystem = 'PORTAL' | 'BITRIX24' | string | null | undefined;
type DuplicateRow = {
  id: string;
  name?: string | null;
  full_name?: string | null;
  company_name?: string | null;
  inn?: string | null;
  source_system?: SourceSystem;
  sync_status?: string | null;
  is_active: boolean;
  reasons?: string[];
};

const MOVED_NAMES: Record<string, string> = {
  contacts: 'контакты', tickets: 'заявки', calls: 'звонки', conversations: 'диалоги', notes: 'заметки',
  channels: 'каналы связи', billing_periods: 'расчётные периоды', billing_conflicts: 'совпадающие расчётные периоды', tariffs: 'настройка тарифа',
};

export function resolveMergeDirection(currentId: string, currentSource: SourceSystem, candidateId: string, candidateSource: SourceSystem) {
  if (currentSource === 'BITRIX24' && candidateSource === 'BITRIX24') {
    return { survivorId: currentId, duplicateId: candidateId, switchesCard: false, blocked: true };
  }
  if (candidateSource === 'BITRIX24' && currentSource !== 'BITRIX24') {
    return { survivorId: candidateId, duplicateId: currentId, switchesCard: true, blocked: false };
  }
  return { survivorId: currentId, duplicateId: candidateId, switchesCard: false, blocked: false };
}

function valueText(field: DirectoryCustomField) {
  if (field.value === undefined || field.value === null || field.value === '') return 'Не заполнено';
  if (typeof field.value === 'boolean') return field.value ? 'Да' : 'Нет';
  if (Array.isArray(field.value)) return field.value.join(', ');
  if (field.value_type === 'DATE' || field.value_type === 'DATETIME') {
    const date = new Date(String(field.value));
    if (!Number.isNaN(date.getTime())) return field.value_type === 'DATE' ? date.toLocaleDateString('ru-RU') : date.toLocaleString('ru-RU');
  }
  return String(field.value);
}

function options(field: DirectoryCustomField) {
  return Array.isArray(field.enum_options) ? field.enum_options.map(item => String(typeof item === 'object' && item !== null ? (item as { value?: unknown }).value ?? '' : item)).filter(Boolean) : [];
}

function inputValue(field: DirectoryCustomField, raw: FormDataEntryValue | null, all: FormData) {
  if (field.value_type === 'BOOLEAN') return raw === 'true';
  if (field.value_type === 'NUMBER') return raw === null || String(raw).trim() === '' ? null : Number(raw);
  if (field.value_type === 'MULTI_ENUM') return all.getAll(field.schema_id).map(String);
  return String(raw ?? '');
}

export function DirectoryCustomFields({ entityType, entityId, initialFields = [], isAdmin }: {
  entityType: EntityType;
  entityId: string;
  initialFields?: DirectoryCustomField[];
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState('');
  const queryKey = ['directory-fields', entityType, entityId];
  const { data = initialFields } = useQuery({
    queryKey,
    queryFn: () => api<DirectoryCustomField[]>(`/directory/${entityType}/${entityId}/fields`),
    initialData: initialFields,
  });
  const portalFields = data.filter(field => field.owner === 'PORTAL');
  const mutation = useMutation({
    mutationFn: (values: Array<{ schemaId: string; value: unknown }>) => api<DirectoryCustomField[]>(`/directory/${entityType}/${entityId}/fields`, { method: 'PUT', body: JSON.stringify({ values }) }),
    onSuccess: fields => {
      queryClient.setQueryData(queryKey, fields);
      setEditing(false);
      setMessage('Дополнительные поля сохранены');
    },
    onError: reason => setMessage(reason instanceof Error ? reason.message : 'Не удалось сохранить поля'),
  });
  if (!data.length) return null;
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    mutation.mutate(portalFields.map(field => ({ schemaId: field.schema_id, value: inputValue(field, form.get(field.schema_id), form) })));
  }
  return <section className="directory-extra-fields">
    <header><div><h3>Дополнительные поля</h3><p>Поля портала и Битрикс24 с указанием владельца</p></div>{isAdmin && portalFields.length > 0 && !editing && <button className="soft-button" onClick={() => { setMessage(''); setEditing(true); }}><PencilLine /> Изменить</button>}</header>
    {editing ? <form onSubmit={submit}>
      <div className="directory-field-editor">{portalFields.map(field => <label key={field.schema_id}><span>{field.label}{field.is_required ? ' *' : ''}</span>{field.value_type === 'BOOLEAN'
        ? <select name={field.schema_id} defaultValue={String(Boolean(field.value))}><option value="false">Нет</option><option value="true">Да</option></select>
        : field.value_type === 'ENUM'
          ? <select name={field.schema_id} defaultValue={String(field.value ?? '')}><option value="">Не выбрано</option>{options(field).map(option => <option key={option}>{option}</option>)}</select>
          : field.value_type === 'MULTI_ENUM'
            ? <select name={field.schema_id} multiple defaultValue={Array.isArray(field.value) ? field.value.map(String) : []}>{options(field).map(option => <option key={option}>{option}</option>)}</select>
            : <input name={field.schema_id} type={field.value_type === 'NUMBER' ? 'number' : field.value_type === 'DATE' ? 'date' : field.value_type === 'DATETIME' ? 'datetime-local' : field.value_type === 'URL' ? 'url' : 'text'} defaultValue={String(field.value ?? '')} required={field.is_required} />}</label>)}</div>
      <div className="directory-field-actions"><button type="button" className="button ghost compact" onClick={() => setEditing(false)} disabled={mutation.isPending}><X /> Отмена</button><button className="button primary compact" disabled={mutation.isPending}><Save /> {mutation.isPending ? 'Сохраняем…' : 'Сохранить'}</button></div>
    </form> : <dl>{data.map(field => <div key={field.schema_id}><dt>{field.label}<small className={field.owner === 'BITRIX24' ? 'bitrix' : ''}>{field.owner === 'BITRIX24' ? <><LockKeyhole /> Битрикс24</> : 'Портал'}</small></dt><dd>{valueText(field)}</dd></div>)}</dl>}
    {message && <p className={mutation.isError ? 'directory-inline-error' : 'directory-inline-success'}>{message}</p>}
  </section>;
}

const ACTION_NAMES: Record<string, string> = {
  DIRECTORY_CUSTOM_FIELDS_UPDATED: 'Изменены дополнительные поля',
  DIRECTORY_ENTITIES_MERGED: 'Объединены дубли',
  DIRECTORY_ENTITY_MERGED_INTO: 'Карточка объединена с основной',
  COMPANY_CREATED: 'Создана карточка компании',
  COMPANY_UPDATED: 'Изменена карточка компании',
  COMPANY_ARCHIVED: 'Компания архивирована',
  COMPANY_DELETED: 'Компания удалена из рабочего списка',
  CONTACT_CREATED: 'Создана карточка контакта',
  CONTACT_UPDATED: 'Изменена карточка контакта',
  CONTACT_ARCHIVED: 'Контакт архивирован',
  BITRIX24_COMPANY_SYNCED: 'Данные компании обновлены из Битрикс24',
  BITRIX24_CONTACT_SYNCED: 'Данные контакта обновлены из Битрикс24',
  BITRIX24_COMPANY_ARCHIVED: 'Компания архивирована по данным Битрикс24',
  BITRIX24_CONTACT_ARCHIVED: 'Контакт архивирован по данным Битрикс24',
};

const FIELD_NAMES: Record<string, string> = {
  name: 'название', full_name: 'полное название', legal_form: 'юридическая форма', inn: 'ИНН', kpp: 'КПП', ogrn: 'ОГРН',
  address: 'адрес', postal_address: 'почтовый адрес', website: 'сайт', description: 'описание', infrastructure_description: 'инфраструктура',
  responsible_user_id: 'ответственный', service_level: 'уровень обслуживания', is_active: 'статус', company_id: 'компания', position: 'должность',
  department: 'подразделение', phone: 'телефон', email: 'email', contact_type: 'тип контакта', notes: 'заметки', preferred_channel: 'предпочтительный канал',
  working_hours: 'рабочее время', timezone: 'часовой пояс', is_primary: 'основной контакт', channels: 'каналы связи',
};

function safeChangeDetails(change: DirectoryChange) {
  const fields = Array.isArray(change.metadata?.changedFields) ? change.metadata.changedFields.filter(item => typeof item === 'string').slice(0, 20) as string[] : [];
  return fields.length ? `Поля: ${fields.map(field => FIELD_NAMES[field] || field).join(', ')}` : '';
}

export function DirectoryChangeHistory({ entityType, entityId, initialHistory = [] }: {
  entityType: EntityType;
  entityId: string;
  initialHistory?: DirectoryChange[];
}) {
  const { data = initialHistory, isLoading } = useQuery({
    queryKey: ['directory-history', entityType, entityId],
    queryFn: () => api<DirectoryChange[]>(`/directory/${entityType}/${entityId}/history`),
    initialData: initialHistory,
  });
  return <section className="directory-change-history"><header><span><History /></span><div><h3>Изменения карточки</h3><p>Кто, когда и откуда обновил данные</p></div></header>{isLoading ? <p>Загрузка истории…</p> : data.length ? <ol>{data.map(change => <li key={change.id}><i /><div><b>{ACTION_NAMES[change.action] || change.action}</b><span>{change.actor_name || (change.action.startsWith('BITRIX24_') ? 'Битрикс24' : 'Система')} · {new Date(change.created_at).toLocaleString('ru-RU')}</span>{safeChangeDetails(change) && <small>{safeChangeDetails(change)}</small>}</div></li>)}</ol> : <p>Изменений пока нет.</p>}</section>;
}

export function DirectoryDuplicates({ entityType, entityId, entitySource, onMerged }: {
  entityType: EntityType;
  entityId: string;
  entitySource?: SourceSystem;
  onMerged?: (survivorId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<{ candidate: DuplicateRow; counts: Record<string, number>; direction: ReturnType<typeof resolveMergeDirection> } | null>(null);
  const [message, setMessage] = useState('');
  const queryKey = ['directory-duplicates', entityType, entityId];
  const { data = [], isLoading } = useQuery({ queryKey, queryFn: () => api<DuplicateRow[]>(`/directory/${entityType}/${entityId}/duplicates`) });
  const previewMutation = useMutation({
    mutationFn: async (candidate: DuplicateRow) => {
      const direction = resolveMergeDirection(entityId, entitySource, candidate.id, candidate.source_system);
      if (direction.blocked) throw new Error('Две разные master-карточки Битрикс24 объединять нельзя');
      const counts = await api<Record<string, number>>(`/directory/${entityType}/${direction.survivorId}/merge-preview`, { method: 'POST', body: JSON.stringify({ duplicateId: direction.duplicateId }) });
      return { candidate, counts, direction };
    },
    onSuccess: setPreview,
    onError: reason => setMessage(reason instanceof Error ? reason.message : 'Не удалось проверить объединение'),
  });
  const mergeMutation = useMutation({
    mutationFn: () => {
      if (!preview) throw new Error('Сначала проверьте объединение');
      return api<{ survivorId: string }>(`/directory/${entityType}/${preview.direction.survivorId}/merge`, { method: 'POST', body: JSON.stringify({ duplicateId: preview.direction.duplicateId }) });
    },
    onSuccess: async result => {
      setPreview(null);
      setMessage('Карточки безопасно объединены, связи и история сохранены');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['companies'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['directory-duplicates'] }),
      ]);
      onMerged?.(result.survivorId);
    },
    onError: reason => setMessage(reason instanceof Error ? reason.message : 'Не удалось объединить карточки'),
  });
  const countSummary = useMemo(() => preview ? Object.entries(preview.counts).filter(([, value]) => Number(value) > 0) : [], [preview]);
  return <section className={`directory-duplicates ${data.length ? 'has-items' : ''}`}>
    <button className="directory-duplicates-toggle" onClick={() => setOpen(value => !value)}><span><GitMerge /><span><b>Проверка дублей</b><small>{isLoading ? 'Идёт проверка…' : data.length ? `Найдено: ${data.length}` : 'Совпадений не найдено'}</small></span></span>{open ? <ChevronUp /> : <ChevronDown />}</button>
    {open && <div className="directory-duplicates-body">{data.map(candidate => <article key={candidate.id}><div><b>{candidate.name || candidate.full_name || 'Без названия'}</b><span>{candidate.company_name || candidate.inn || (candidate.source_system === 'BITRIX24' ? 'Битрикс24' : 'Локальная карточка')}</span><p>{candidate.reasons?.join(' · ') || 'Есть совпадающие данные'}</p></div><button className="button ghost compact" onClick={() => { setMessage(''); previewMutation.mutate(candidate); }} disabled={previewMutation.isPending}>Проверить</button></article>)}{!data.length && <p>Карточек с совпадающими реквизитами или каналами связи нет.</p>}</div>}
    {preview && <div className="directory-merge-preview"><AlertTriangle /><div><b>Перед объединением</b><p>{preview.direction.switchesCard ? 'Основной останется master-карточка Битрикс24. ' : ''}{countSummary.length ? `Будут обработаны: ${countSummary.map(([key, value]) => `${MOVED_NAMES[key] || key} — ${value}`).join(', ')}.` : 'Связанные записи будут проверены и перенесены.'} Несовпадающие расчётные периоды будут перенесены, а совпадающие сохранятся у архивной карточки без удаления данных. Дубль останется в архиве.</p><div><button className="button ghost compact" onClick={() => setPreview(null)}>Отмена</button><button className="button primary compact" onClick={() => mergeMutation.mutate()} disabled={mergeMutation.isPending}><Check /> {mergeMutation.isPending ? 'Объединяем…' : 'Объединить'}</button></div></div></div>}
    {message && <p className={previewMutation.isError || mergeMutation.isError ? 'directory-inline-error' : 'directory-inline-success'}>{message}</p>}
  </section>;
}
